<template>
  <AccordionCardInfoDisplay
    v-if="showForEntityTypes.includes(clickedEntityType)"
    :heading="infoTitle"
    :isExpanded="isExpanded"
    @onClicked="handleClick"
  >
    <template #content>
      <div v-if="!isLoading && infoData?.length && !allItemsMissingContent" class="container">
        <div v-for="(item, index) in infoData" :key="index" class="item">
          <DividerHorizontal v-if="item.content && item.hasDivider" style="margin: 8px 0" />
          <div v-if="item.poiProduct" style="height: 8px" />
          <header v-if="item.content" class="title">
            {{ item.title }}
            <TooltipStandardV2
              v-if="item.info"
              class="title__info"
              :text="item.info"
              :arrowPosition="'bottom-center'"
              :vertical-offset="2"
            >
              <IconInfoCircle class="title__info" />
            </TooltipStandardV2>
          </header>
          <BadgeSquare
            v-if="item.isBadge && item.content"
            style="margin-top: 4px"
            :badgeText="item.content"
            size="small"
          />
          <section v-else-if="item.content" class="content">{{ item.content }}</section>
          <div v-if="item.poiProduct" class="product-button">
            <ButtonOutline
              :iconLeft="iconArrowUpRight"
              :buttonText="t('general__text__product-details')"
              size="small"
              style="margin: 16px 0 0 0; width: 100%"
              @onClicked="() => openProductDetails()"
            />
          </div>
        </div>
      </div>
      <div
        v-else-if="!isLoading && (allItemsMissingContent || !infoData?.length)"
        class="container"
      >
        <div class="no-data">
          <p>{{ t('general__data__no-data-published') }}</p>
        </div>
      </div>
      <div v-else class="no-data">
        <PulseLoader :loading="true" color="#3F46D6" />
      </div>
    </template>
  </AccordionCardInfoDisplay>
</template>
<script lang="ts" setup>
import AccordionCardInfoDisplay from './AccordionCardInfoDisplay.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import IconInfoCircle from '@/design-system/assets/icons/IconInfoCircle.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useSelectionStore } from '@/pinia/selection'
import { useI18n } from 'vue-i18n'
import { CoreEntityDto } from '@/interfaces/core.interface'
import { InfoData } from '@/utils/entityInformation'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import { useStore } from 'vuex'
import iconArrowUpRight from '@/design-system/assets/icons/Icon-Arrow-up-right.svg'

const props = defineProps<{
  showForEntityTypes: string[]
  currentEntity: CoreEntityDto
  infoData: InfoData[]
  isLoading: boolean
}>()
const { t } = useI18n()
const store = useStore()
const selectionStore = useSelectionStore()
const { clickedEntityType } = storeToRefs(selectionStore)
const isExpanded = ref(false)

const infoTitle = computed(() => {
  switch (selectionStore.clickedEntityType) {
    case 'COMPLEX':
      return t('general__title__complex-information')
    case 'REAL_ESTATE':
      return t('general__title__realestate-information')
    case 'CONSTRUCTION':
      return t('general__title__construction-information')
    case 'LEVEL':
      return t('general__title__level-information')
    case 'SPACE':
      return t('general__title__space-information')
    case 'POI':
      return t('general__title__poi-information')
    case 'LINE':
      return t('general__title__line-information')
    case 'ZONE':
      return t('general__title__zone-information')
    default:
      return ''
  }
})
function handleClick() {
  isExpanded.value = !isExpanded.value
}
function openProductDetails() {
  store.commit('SET_STATUS_ON_SHOW_OVERLAY_MODAL', { toggleModal: true })
  store.commit('SET_CURRENT_OVERLAY_MODAL', { modalName: 'product' })
}
const allItemsMissingContent = computed(() => {
  return props.infoData.every((item) => !item.content && !item.poiProduct)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';

.container {
  padding-top: 4px;
  height: fit-content;
  overflow: hidden;
  flex: 1 1 auto;
  min-height: calc((40px * 1) + 16px); // 1 rows + 16px padding
  z-index: 1;
  width: 100%;
  .item:not(:last-child) {
    padding-bottom: 8px;
  }
  .product-button {
    padding: 0 3px 3px;
  }
  .title {
    @include font-mixins.desktop-bold-label;
    color: variables.$color-greyscale-secondary;
    display: flex;
    align-items: center;
    gap: 4px;
    &__info {
      height: 16px;
      min-width: 16px;
      max-width: 16px;
    }
  }
  .content {
    @include font-mixins.desktop-regular-paragraph;
    color: variables.$color-greyscale-content;
    white-space: pre-line;
  }
}
.no-data {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    @include font-mixins.desktop-regular-label;
  }
}
</style>
